import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import {
  Gift,
  Target,
  Calendar,
  Send,
  AlertCircle,
  CheckCircle,
  Share2,
  Clock,
  Users,
  Heart,
  MessageSquare,
  Repeat,
  ArrowRight,
  ArrowLeft,
  Sparkles,
  Eye,
  BarChart2
} from 'lucide-react';
import { supabase } from '../lib/supabase';
import { streamOpenAIResponse } from '../lib/openai';

interface GiveawayForm {
  title: string;
  goal: string;
  platforms: string[];
  prizeDescription: string;
  entryMethods: string[];
  startDate: string;
  endDate: string;
  sendReminders: boolean;
}

const GiveawayCampaign = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0);
  const [form, setForm] = useState<GiveawayForm>({
    title: '',
    goal: '',
    platforms: [],
    prizeDescription: '',
    entryMethods: [],
    startDate: '',
    endDate: '',
    sendReminders: true
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [generatedContent, setGeneratedContent] = useState<{
    posts: Array<{
      content: string;
      platform: string;
      scheduledFor?: string;
    }>;
    reminders: string[];
    winnerAnnouncement: string;
  } | null>(null);

  const goals = [
    { id: 'followers', label: 'Grow Followers', icon: Users },
    { id: 'engagement', label: 'Boost Engagement', icon: Heart },
    { id: 'leads', label: 'Collect Leads', icon: Target }
  ];

  const platforms = [
    { id: 'twitter', label: 'Twitter', icon: 'twitter' },
    { id: 'instagram', label: 'Instagram', icon: 'instagram' },
    { id: 'facebook', label: 'Facebook', icon: 'facebook' },
    { id: 'linkedin', label: 'LinkedIn', icon: 'linkedin' },
    { id: 'youtube', label: 'YouTube', icon: 'youtube' }
  ];

  const entryMethods = [
    { id: 'follow', label: 'Follow Account', icon: Users },
    { id: 'like', label: 'Like Post', icon: Heart },
    { id: 'comment', label: 'Comment', icon: MessageSquare },
    { id: 'share', label: 'Share/Retweet', icon: Share2 },
    { id: 'tag', label: 'Tag Friends', icon: Users }
  ];

  const handleSubmit = async () => {
    try {
      setLoading(true);
      setError(null);

      // Validate dates
      const start = new Date(form.startDate);
      const end = new Date(form.endDate);
      if (end <= start) {
        throw new Error('End date must be after start date');
      }

      // Get current user
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      if (userError) throw userError;
      if (!user) throw new Error('Not authenticated');

      // Save giveaway campaign
      const { data: campaign, error: campaignError } = await supabase
        .from('giveaway_campaigns')
        .insert({
          user_id: user.id,
          title: form.title,
          goal: form.goal,
          platforms: form.platforms,
          prize_description: form.prizeDescription,
          entry_methods: form.entryMethods,
          start_date: form.startDate,
          end_date: form.endDate,
          send_reminders: form.sendReminders,
          status: 'draft'
        })
        .select()
        .single();

      if (campaignError) throw campaignError;

      // Generate content using OpenAI
      const prompt = `Create a giveaway campaign for ${form.title}.

Prize: ${form.prizeDescription}
Platforms: ${form.platforms.join(', ')}
Entry Methods: ${form.entryMethods.join(', ')}
Duration: ${form.startDate} to ${form.endDate}

Format your response as JSON with the following structure:
{
  "posts": [
    {
      "content": "announcement post content",
      "platform": "platform name",
      "scheduledFor": "posting time"
    }
  ],
  "reminders": [
    "mid-campaign reminder post",
    "last day reminder post"
  ],
  "winnerAnnouncement": "winner announcement template"
}`;

      let responseText = '';

      await streamOpenAIResponse(
        [{ role: 'user', content: prompt }],
        (chunk) => {
          responseText += chunk;
          try {
            const data = JSON.parse(responseText);
            setGeneratedContent(data);
          } catch (e) {
            // Partial response, continue collecting
          }
        },
        (error) => {
          throw error;
        }
      );

    } catch (err) {
      console.error('Error creating giveaway:', err);
      setError(err instanceof Error ? err.message : 'Failed to create giveaway');
    } finally {
      setLoading(false);
    }
  };

  const renderStep = () => {
    switch (currentStep) {
      case 0:
        return (
          <div className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Giveaway Title
              </label>
              <input
                type="text"
                value={form.title}
                onChange={(e) => setForm(prev => ({ ...prev, title: e.target.value }))}
                placeholder="Enter a catchy title for your giveaway"
                className="block w-full rounded-lg border-gray-300 dark:border-dark-600 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 dark:bg-dark-700 dark:text-white sm:text-sm"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                What's your goal?
              </label>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                {goals.map(goal => {
                  const Icon = goal.icon;
                  return (
                    <motion.button
                      key={goal.id}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={() => setForm(prev => ({ ...prev, goal: goal.id }))}
                      className={`flex items-center p-4 rounded-lg border-2 transition-all ${
                        form.goal === goal.id
                          ? 'border-indigo-500 bg-indigo-50 dark:bg-indigo-900/20'
                          : 'border-gray-200 dark:border-dark-700 hover:border-indigo-300 dark:hover:border-indigo-700'
                      }`}
                    >
                      <div className={`p-2 rounded-lg ${
                        form.goal === goal.id
                          ? 'bg-indigo-100 dark:bg-indigo-900/40 text-indigo-600 dark:text-indigo-400'
                          : 'bg-gray-100 dark:bg-dark-700 text-gray-500 dark:text-gray-400'
                      }`}>
                        <Icon className="h-5 w-5" />
                      </div>
                      <span className="ml-3 font-medium text-gray-900 dark:text-white">
                        {goal.label}
                      </span>
                    </motion.button>
                  );
                })}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Prize Description
              </label>
              <textarea
                value={form.prizeDescription}
                onChange={(e) => setForm(prev => ({ ...prev, prizeDescription: e.target.value }))}
                placeholder="Describe what you're giving away"
                rows={4}
                className="block w-full rounded-lg border-gray-300 dark:border-dark-600 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 dark:bg-dark-700 dark:text-white sm:text-sm"
              />
            </div>
          </div>
        );

      case 1:
        return (
          <div className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Select Platforms
              </label>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                {platforms.map(platform => (
                  <motion.button
                    key={platform.id}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => setForm(prev => ({
                      ...prev,
                      platforms: prev.platforms.includes(platform.id)
                        ? prev.platforms.filter(p => p !== platform.id)
                        : [...prev.platforms, platform.id]
                    }))}
                    className={`flex items-center p-4 rounded-lg border-2 transition-all ${
                      form.platforms.includes(platform.id)
                        ? 'border-indigo-500 bg-indigo-50 dark:bg-indigo-900/20'
                        : 'border-gray-200 dark:border-dark-700 hover:border-indigo-300 dark:hover:border-indigo-700'
                    }`}
                  >
                    <span className="font-medium text-gray-900 dark:text-white">
                      {platform.label}
                    </span>
                    {form.platforms.includes(platform.id) && (
                      <CheckCircle className="ml-auto h-5 w-5 text-indigo-600 dark:text-indigo-400" />
                    )}
                  </motion.button>
                ))}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Entry Methods
              </label>
              <div className="grid grid-cols-2 gap-4">
                {entryMethods.map(method => {
                  const Icon = method.icon;
                  return (
                    <motion.button
                      key={method.id}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={() => setForm(prev => ({
                        ...prev,
                        entryMethods: prev.entryMethods.includes(method.id)
                          ? prev.entryMethods.filter(m => m !== method.id)
                          : [...prev.entryMethods, method.id]
                      }))}
                      className={`flex items-center p-4 rounded-lg border-2 transition-all ${
                        form.entryMethods.includes(method.id)
                          ? 'border-indigo-500 bg-indigo-50 dark:bg-indigo-900/20'
                          : 'border-gray-200 dark:border-dark-700 hover:border-indigo-300 dark:hover:border-indigo-700'
                      }`}
                    >
                      <div className={`p-2 rounded-lg ${
                        form.entryMethods.includes(method.id)
                          ? 'bg-indigo-100 dark:bg-indigo-900/40 text-indigo-600 dark:text-indigo-400'
                          : 'bg-gray-100 dark:bg-dark-700 text-gray-500 dark:text-gray-400'
                      }`}>
                        <Icon className="h-5 w-5" />
                      </div>
                      <span className="ml-3 font-medium text-gray-900 dark:text-white">
                        {method.label}
                      </span>
                    </motion.button>
                  );
                })}
              </div>
            </div>
          </div>
        );

      case 2:
        return (
          <div className="space-y-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Start Date
                </label>
                <input
                  type="date"
                  value={form.startDate}
                  onChange={(e) => setForm(prev => ({ ...prev, startDate: e.target.value }))}
                  min={new Date().toISOString().split('T')[0]}
                  className="block w-full rounded-lg border-gray-300 dark:border-dark-600 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 dark:bg-dark-700 dark:text-white sm:text-sm"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  End Date
                </label>
                <input
                  type="date"
                  value={form.endDate}
                  onChange={(e) => setForm(prev => ({ ...prev, endDate: e.target.value }))}
                  min={form.startDate || new Date().toISOString().split('T')[0]}
                  className="block w-full rounded-lg border-gray-300 dark:border-dark-600 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 dark:bg-dark-700 dark:text-white sm:text-sm"
                />
              </div>
            </div>

            <div className="flex items-center">
              <input
                type="checkbox"
                id="reminders"
                checked={form.sendReminders}
                onChange={(e) => setForm(prev => ({ ...prev, sendReminders: e.target.checked }))}
                className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
              />
              <label htmlFor="reminders" className="ml-2 block text-sm text-gray-900 dark:text-gray-300">
                Send reminders as end date approaches
              </label>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  const renderResults = () => {
    if (!generatedContent) return null;

    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="space-y-8"
      >
        <div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
            Your Giveaway Campaign
          </h2>
          <p className="text-gray-600 dark:text-gray-400">
            Here's your complete giveaway campaign plan
          </p>
        </div>

        {/* Analytics Preview */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
          <div className="bg-indigo-50 dark:bg-indigo-900/20 rounded-xl p-6">
            <div className="flex items-center space-x-2 mb-2">
              <Eye className="h-5 w-5 text-indigo-600 dark:text-indigo-400" />
              <h3 className="font-medium text-indigo-900 dark:text-indigo-100">
                Expected Reach
              </h3>
            </div>
            <p className="text-3xl font-bold text-indigo-600 dark:text-indigo-400">
              5K-10K
            </p>
          </div>

          <div className="bg-green-50 dark:bg-green-900/20 rounded-xl p-6">
            <div className="flex items-center space-x-2 mb-2">
              <Users className="h-5 w-5 text-green-600 dark:text-green-400" />
              <h3 className="font-medium text-green-900 dark:text-green-100">
                Est. Participants
              </h3>
            </div>
            <p className="text-3xl font-bold text-green-600 dark:text-green-400">
              200-500
            </p>
          </div>

          <div className="bg-purple-50 dark:bg-purple-900/20 rounded-xl p-6">
            <div className="flex items-center space-x-2 mb-2">
              <BarChart2 className="h-5 w-5 text-purple-600 dark:text-purple-400" />
              <h3 className="font-medium text-purple-900 dark:text-purple-100">
                Engagement Rate
              </h3>
            </div>
            <p className="text-3xl font-bold text-purple-600 dark:text-purple-400">
              4-8%
            </p>
          </div>
        </div>

        {/* Announcement Posts */}
        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center">
            <Gift className="h-5 w-5 mr-2 text-indigo-600 dark:text-indigo-400" />
            Announcement Posts
          </h3>
          {generatedContent.posts.map((post, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: index * 0.1 }}
              className="bg-white dark:bg-dark-800 rounded-xl p-6 border border-gray-100 dark:border-dark-700"
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center space-x-2 mb-3">
                    <span className="px-2 py-1 bg-indigo-100 dark:bg-indigo-900/20 text-indigo-600 dark:text-indigo-400 rounded-full text-sm">
                      {post.platform}
                    </span>
                    {post.scheduledFor && (
                      <span className="flex items-center text-sm text-gray-500 dark:text-gray-400">
                        <Clock className="h-4 w-4 mr-1" />
                        {post.scheduledFor}
                      </span>
                    )}
                  </div>
                  <p className="text-gray-900 dark:text-white">{post.content}</p>
                </div>
                <div className="flex items-center space-x-2 ml-4">
                  <button className="p-2 text-gray-400 hover:text-indigo-600 dark:hover:text-indigo-400 rounded-lg hover:bg-gray-100 dark:hover:bg-dark-700">
                    <Share2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Reminder Posts */}
        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center">
            <Clock className="h-5 w-5 mr-2 text-indigo-600 dark:text-indigo-400" />
            Reminder Posts
          </h3>
          {generatedContent.reminders.map((reminder, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: index * 0.1 }}
              className="bg-white dark:bg-dark-800 rounded-xl p-6 border border-gray-100 dark:border-dark-700"
            >
              <p className="text-gray-900 dark:text-white">{reminder}</p>
            </motion.div>
          ))}
        </div>

        {/* Winner Announcement Template */}
        <div className="bg-gradient-to-r from-indigo-500 to-purple-600 rounded-xl p-6 text-white">
          <div className="flex items-center space-x-3 mb-4">
            <Gift className="h-5 w-5" />
            <h3 className="text-lg font-semibold">Winner Announcement Template</h3>
          </div>
          <p className="text-indigo-100">{generatedContent.winnerAnnouncement}</p>
        </div>

        <div className="flex justify-between pt-6 border-t border-gray-200 dark:border-dark-700">
          <button
            onClick={() => {
              setGeneratedContent(null);
              setCurrentStep(0);
              setForm({
                title: '',
                goal: '',
                platforms: [],
                prizeDescription: '',
                entryMethods: [],
                startDate: '',
                endDate: '',
                sendReminders: true
              });
            }}
            className="px-4 py-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
          >
            Start Over
          </button>
          <div className="space-x-4">
            <button
              onClick={handleSubmit}
              className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors flex items-center"
            >
              <Sparkles className="h-4 w-4 mr-2" />
              Generate More
            </button>
            <button
              onClick={() => navigate('/calendar')}
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center"
            >
              <Calendar className="h-4 w-4 mr-2" />
              Schedule All
            </button>
          </div>
        </div>
      </motion.div>
    );
  };

  return (
    <div className="max-w-4xl mx-auto">
      {error && (
        <div className="mb-6 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 flex items-center space-x-2">
          <AlertCircle className="h-5 w-5 text-red-500 dark:text-red-400" />
          <p className="text-sm text-red-600 dark:text-red-300">{error}</p>
        </div>
      )}

      {loading ? (
        <div className="flex flex-col items-center justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
          <p className="mt-4 text-gray-600 dark:text-gray-400">Creating your giveaway campaign...</p>
        </div>
      ) : generatedContent ? (
        renderResults()
      ) : (
        <div className="space-y-8">
          {/* Progress bar */}
          <div className="relative pt-1">
            <div className="flex mb-2 items-center justify-between">
              <div>
                <span className="text-xs font-semibold inline-block py-1 px-2 uppercase rounded-full text-indigo-600 bg-indigo-200 dark:bg-indigo-900/40 dark:text-indigo-400">
                  Progress
                </span>
              </div>
              <div className="text-right">
                <span className="text-xs font-semibold inline-block text-indigo-600 dark:text-indigo-400">
                  {Math.round((currentStep / 2) * 100)}%
                </span>
              </div>
            </div>
            <div className="overflow-hidden h-2 mb-4 text-xs flex rounded bg-indigo-200 dark:bg-indigo-900/40">
              <motion.div
                initial={{ width: 0 }}
                animate={{ width: `${(currentStep / 2) * 100}%` }}
                className="shadow-none flex flex-col text-center whitespace-nowrap text-white justify-center bg-indigo-600"
              />
            </div>
          </div>

          {renderStep()}

          <div className="flex justify-between pt-6">
            {currentStep > 0 ? (
              <button
                onClick={() => setCurrentStep(prev => prev - 1)}
                className="flex items-center px-4 py-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back
              </button>
            ) : (
              <button
                onClick={() => navigate('/campaign-generator')}
                className="flex items-center px-4 py-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Cancel
              </button>
            )}
            <button
              onClick={() => {
                if (currentStep < 2) {
                  setCurrentStep(prev => prev + 1);
                } else {
                  handleSubmit();
                }
              }}
              disabled={
                currentStep === 0 ? !form.title || !form.goal || !form.prizeDescription :
                currentStep === 1 ? form.platforms.length === 0 || form.entryMethods.length === 0 :
                !form.startDate || !form.endDate
              }
              className={`flex items-center px-4 py-2 rounded-lg transition-colors ${
                (currentStep === 0 && form.title && form.goal && form.prizeDescription) ||
                (currentStep === 1  && form.platforms.length > 0 && form.entryMethods.length > 0) ||
                (currentStep === 2 && form.startDate && form.endDate)
                  ? 'bg-indigo-600 text-white hover:bg-indigo-700'
                  : 'bg-gray-300 dark:bg-dark-700 text-gray-500 dark:text-gray-400 cursor-not-allowed'
              }`}
            >
              {currentStep === 2 ? (
                <>
                  Create Giveaway
                  <Gift className="h-4 w-4 ml-2" />
                </>
              ) : (
                <>
                  Next
                  <ArrowRight className="h-4 w-4 ml-2" />
                </>
              )}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default GiveawayCampaign;